import { useState, useEffect } from 'react';
import { escrowAPI } from '../services/api';
import '../styles/EscrowStatus.css';

const EscrowStatus = ({ orderId, userRole = 'buyer', onStatusChange }) => {
  const [escrow, setEscrow] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [actionMessage, setActionMessage] = useState(null);

  useEffect(() => {
    fetchEscrowStatus();
  }, [orderId]);

  const fetchEscrowStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch status and timeline in parallel
      const [statusRes, timelineRes] = await Promise.allSettled([
        escrowAPI.getEscrowStatus(orderId),
        escrowAPI.getEscrowTimeline(orderId),
      ]);

      if (statusRes.status === 'fulfilled' && statusRes.value?.data) {
        setEscrow(statusRes.value.data);
      }
      if (timelineRes.status === 'fulfilled') {
        const events = timelineRes.value?.data?.data || timelineRes.value?.data || [];
        setTimeline(Array.isArray(events) ? events : []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load escrow status');
    } finally {
      setLoading(false);
    }
  };

  // Pandascrow-held escrows require the OTP the buyer received before funds
  // can release; internal (Monnify ledger) escrows release immediately.
  const handleConfirmDeliveryClick = () => {
    if (escrow?.provider === 'pandascrow') {
      setShowOTPModal(true);
      return;
    }
    handleConfirmDelivery();
  };

  const handleConfirmDelivery = async (otp) => {
    if (!otp && !window.confirm('Are you sure you want to confirm delivery? This will release payment to the seller.')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await escrowAPI.confirmDelivery(orderId, otp);

      setActionMessage({ type: 'success', text: response?.data?.message || response?.message || 'Delivery confirmed! Payment is being released to the seller.' });
      setShowOTPModal(false);
      setOtpCode('');

      await fetchEscrowStatus();
      if (onStatusChange) onStatusChange('released');
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message || 'Failed to confirm delivery' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestRefund = async () => {
    if (!refundReason.trim()) {
      setActionMessage({ type: 'error', text: 'Please provide a reason for the refund' });
      return;
    }

    try {
      setActionLoading(true);
      const response = await escrowAPI.requestEscrowRefund(orderId, refundReason);

      // Message differs by provider: internal = refunded immediately,
      // pandascrow = dispute opened, resolution pending externally
      setActionMessage({ type: 'success', text: response?.data?.message || response?.message || 'Refund request submitted successfully!' });
      setShowRefundModal(false);
      setRefundReason('');

      await fetchEscrowStatus();
      if (onStatusChange) onStatusChange(escrow?.provider === 'pandascrow' ? 'disputed' : 'refunded');
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message || 'Failed to request refund' });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    // Colors matching mobile app implementation
    const statusConfig = {
      pending: { color: '#F59E0B', icon: '⏰', text: 'Pending' },
      held: { color: '#3B82F6', icon: '🔒', text: 'Held in Escrow' },
      released: { color: '#10B981', icon: '✅', text: 'Released' },
      refunded: { color: '#8B5CF6', icon: '↩️', text: 'Refunded' },
      disputed: { color: '#EF4444', icon: '⚠️', text: 'Disputed' },
      cancelled: { color: '#6B7280', icon: '❌', text: 'Cancelled' },
      expired: { color: '#DC2626', icon: '⏱️', text: 'Expired' },
    };

    const config = statusConfig[status] || { color: '#6B7280', icon: '❓', text: status };

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        borderRadius: '20px',
        backgroundColor: `${config.color}20`,
        color: config.color,
        fontSize: '14px',
        fontWeight: '600',
      }}>
        <span>{config.icon}</span>
        <span>{config.text}</span>
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="escrow-status-card">
        <div className="escrow-loading">
          <div className="spinner"></div>
          <p>Loading escrow status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="escrow-status-card">
        <div className="escrow-error">
          <p>⚠️ {error}</p>
        </div>
      </div>
    );
  }

  if (!escrow) {
    return null;
  }

  const isPandascrow = escrow.provider === 'pandascrow';

  return (
    <>
      <div className="escrow-status-card">
        <div className="escrow-header">
          <h3>💰 Escrow Protection</h3>
          {getStatusBadge(escrow.status)}
        </div>

        {actionMessage && (
          <div className={`escrow-action-message escrow-action-message--${actionMessage.type}`}>
            {actionMessage.text}
          </div>
        )}

        <div className="escrow-details">
          <div className="escrow-info-row">
            <span className="label">Escrow Number:</span>
            <span className="value">{escrow.escrow_number}</span>
          </div>

          <div className="escrow-info-row">
            <span className="label">Amount Held:</span>
            <span className="value amount">{formatCurrency(escrow.amount)}</span>
          </div>

          {escrow.escrow_fee > 0 && (
            <div className="escrow-info-row">
              <span className="label">Escrow Fee:</span>
              <span className="value">{formatCurrency(escrow.escrow_fee)}</span>
            </div>
          )}

          <div className="escrow-info-row">
            <span className="label">Net Amount:</span>
            <span className="value amount-highlight">{formatCurrency(escrow.net_amount || escrow.amount)}</span>
          </div>

          <div className="escrow-info-row">
            <span className="label">Created:</span>
            <span className="value">{formatDate(escrow.created_at)}</span>
          </div>

          {escrow.auto_release_date && (
            <div className="escrow-info-row">
              <span className="label">Auto-Release:</span>
              <span className="value">{formatDate(escrow.auto_release_date)}</span>
            </div>
          )}

          {escrow.released_at && (
            <div className="escrow-info-row">
              <span className="label">Released:</span>
              <span className="value">{formatDate(escrow.released_at)}</span>
            </div>
          )}

          {escrow.release_reason && (
            <div className="escrow-info-row">
              <span className="label">Release Reason:</span>
              <span className="value">{escrow.release_reason}</span>
            </div>
          )}
        </div>

        {/* Buyer Actions */}
        {userRole === 'buyer' && escrow.status === 'held' && (
          <div className="escrow-actions">
            <button
              className="btn-confirm-delivery"
              onClick={handleConfirmDeliveryClick}
              disabled={actionLoading}
            >
              {actionLoading ? 'Processing...' : '✅ Confirm Delivery & Release Payment'}
            </button>

            <button
              className="btn-request-refund"
              onClick={() => setShowRefundModal(true)}
              disabled={actionLoading}
            >
              ↩️ {isPandascrow ? 'Open a Dispute' : 'Request Refund'}
            </button>
          </div>
        )}

        {/* Seller Info */}
        {userRole === 'seller' && escrow.status === 'held' && (
          <div className="escrow-seller-info">
            <p className="info-text">
              💡 Payment is securely held in escrow{isPandascrow ? ' by Pandascrow' : ''}. It will be released when the buyer confirms delivery.
            </p>
          </div>
        )}

        {/* Partial Releases */}
        {escrow.partial_releases && escrow.partial_releases.length > 0 && (
          <div className="partial-releases">
            <h4>Partial Releases</h4>
            {escrow.partial_releases.map((release, index) => (
              <div key={index} className="partial-release-item">
                <span className="amount">{formatCurrency(release.amount)}</span>
                <span className="reason">{release.reason}</span>
                <span className="date">{formatDate(release.released_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Escrow Timeline — GET /api/v1/escrow/timeline/:orderId */}
      {timeline.length > 0 && (
        <div className="escrow-status-card" style={{ marginTop: '12px' }}>
          <div className="escrow-header">
            <h3>📋 Escrow Timeline</h3>
          </div>
          <div style={{ position: 'relative', padding: '8px 0' }}>
            {timeline.map((event, i) => (
              <div key={i} style={{ display: 'flex', gap: '14px', marginBottom: i < timeline.length - 1 ? '0' : '0', position: 'relative' }}>
                {/* Vertical line */}
                {i < timeline.length - 1 && (
                  <div style={{ position: 'absolute', left: '11px', top: '26px', bottom: '-8px', width: '2px', background: '#e2e8f0', zIndex: 0 }} />
                )}
                {/* Dot */}
                <div style={{
                  flexShrink: 0, width: '24px', height: '24px', borderRadius: '50%',
                  background: i === 0 ? '#10b981' : '#e2e8f0',
                  border: `2px solid ${i === 0 ? '#10b981' : '#cbd5e1'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '10px', color: i === 0 ? '#fff' : '#94a3b8',
                  zIndex: 1, marginTop: '2px',
                }}>
                  {i === 0 ? '✓' : String(timeline.length - i)}
                </div>
                {/* Content */}
                <div style={{ paddingBottom: '16px', flex: 1 }}>
                  <p style={{ margin: '0 0 2px', fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
                    {event.action || event.event}
                  </p>
                  {event.description && (
                    <p style={{ margin: '0 0 2px', fontSize: '13px', color: '#64748b' }}>
                      {event.description}
                    </p>
                  )}
                  {event.timestamp && (
                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
                      {formatDate(event.timestamp)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* OTP Release Modal (Pandascrow-held escrows only) */}
      {showOTPModal && (
        <div className="modal-overlay" onClick={() => setShowOTPModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delivery</h3>
              <button className="modal-close" onClick={() => setShowOTPModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                Pandascrow sent a one-time code to confirm you've received your order. Enter it below to release payment to the seller.
              </p>

              <div className="form-group">
                <label>One-Time Code *</label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder="Enter the code sent to you"
                  className="refund-reason-input"
                  maxLength={10}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowOTPModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="btn-submit-refund"
                onClick={() => handleConfirmDelivery(otpCode)}
                disabled={actionLoading || !otpCode.trim()}
              >
                {actionLoading ? 'Confirming...' : 'Confirm & Release Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund / Dispute Modal */}
      {showRefundModal && (
        <div className="modal-overlay" onClick={() => setShowRefundModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isPandascrow ? 'Open a Dispute' : 'Request Refund'}</h3>
              <button className="modal-close" onClick={() => setShowRefundModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                {isPandascrow
                  ? "Please describe the issue. Pandascrow will review the dispute and determine how the held funds are resolved."
                  : 'Please provide a reason for requesting a refund. This will cancel the order and return the funds to your wallet.'}
              </p>

              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="E.g., Item not received, wrong item delivered, quality issues..."
                  rows="4"
                  className="refund-reason-input"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowRefundModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="btn-submit-refund"
                onClick={handleRequestRefund}
                disabled={actionLoading || !refundReason.trim()}
              >
                {actionLoading ? 'Submitting...' : isPandascrow ? 'Submit Dispute' : 'Submit Refund Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EscrowStatus;
